import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Section } from 'src/components/Section';
import { GroupFilterControls } from 'src/components/GroupFilterControls';
import { useNotification } from 'src/components/Notification/useNotfication';
import {
    OrganizationRolePermission,
    OrganizationType,
} from 'src/domain/organization';
import {
    useConsumers,
    useMessages,
    useCurrentOrganization,
} from 'src/view/hooks';
import { useCreators } from 'src/view/hooks/creator';
import { useNextOfKins } from 'src/view/hooks/nextOfKin';
import { useOrganizationGroupsById } from 'src/view/hooks/useOrganizationGroupsById';
import {
    buildLeafGroupLabelsById,
    buildParentGroupLabelsByConsumerId,
} from 'src/utils/consumerLabelUtils';
import {
    getPrimaryRole,
    isOrganizationAdministrator,
    UserRole,
} from 'src/domain/user';
import { MessagesList } from './MessagesList';
import { CreateMessageForm } from './CreateMessageForm';
import { TextIcon } from 'src/components/Icons/TextIcon';
import { PlusIcon } from 'src/components/Icons/icons';
import { useSignedInUser } from 'src/view/hooks/useSignedInUser';
import { useCreatorRoleContext } from 'src/view/hooks/useCreatorRoleContext';
import { useCreatorRolePermission } from 'src/view/hooks/useCreatorRolePermission';
import { useOrganizationGroupFilters } from 'src/view/hooks/useOrganizationGroupFilters';
import { badgeVariantToClassName } from 'src/utils/badgeUtils';
import { MessageListToolbar } from './MessageListToolbar';
import { useMessageListUrlFilters } from './useMessageListUrlFilters';

const DEFAULT_PAGE_SIZE = 5;

const getProfileLabel = (
    profile: { firstName?: string; lastName?: string } | undefined,
    fallback: string,
) => {
    const label = `${profile?.firstName ?? ''} ${
        profile?.lastName ?? ''
    }`.trim();

    return label || fallback;
};

export const Messages = () => {
    const { t } = useTranslation();
    const notify = useNotification();
    const user = useSignedInUser();
    const role = getPrimaryRole(user);
    const { organizationId, organization } = useCurrentOrganization();
    const { activeRoleGroupIds, isLoading: isCreatorRoleLoading } =
        useCreatorRoleContext();
    const canSendMessagesToAll = useCreatorRolePermission(
        OrganizationRolePermission.sendMessagesToAll,
    );
    const { consumers } = useConsumers();
    const { creators } = useCreators();
    const { data: nextOfKins } = useNextOfKins({ organizationId });
    const [pageIndex, setPageIndex] = useState(1);
    const listFilters = useMessageListUrlFilters({
        adminFilters:
            isOrganizationAdministrator(user, organizationId) ||
            Boolean(user?.supportMode),
    });
    const { filter: messageReadFilter, month: messageMonth } = listFilters;

    const organizationGroups = useMemo(
        () => organization?.groups ?? [],
        [organization?.groups],
    );
    const hierarchy = useMemo(
        () => organization?.hierarchy ?? [],
        [organization?.hierarchy],
    );
    const {
        filterLevels,
        selectedFilterCount,
        selectedFilterGroupIds: selectedMessageFilterGroupIds,
        setSelectedIdsForLevel,
    } = useOrganizationGroupFilters({
        activeRoleGroupIds,
        groups: organizationGroups,
        hierarchy,
        organizationAdmin: isOrganizationAdministrator(user, organizationId),
        role,
    });

    const messageAllowedGroupIds = useMemo(() => {
        if (selectedMessageFilterGroupIds.length) {
            return selectedMessageFilterGroupIds;
        }

        if (role !== UserRole.creator || !activeRoleGroupIds.length) {
            return undefined;
        }
        return activeRoleGroupIds;
    }, [activeRoleGroupIds, role, selectedMessageFilterGroupIds]);

    const canLoadMessages =
        role !== UserRole.creator ||
        (!isCreatorRoleLoading && activeRoleGroupIds.length > 0);

    const { messages, isLoading, isError, mutate, isValidating, pageInfo } =
        useMessages({
            organizationId: canLoadMessages ? organizationId : undefined,
            allowedGroupIds: messageAllowedGroupIds,
            readStatus: messageReadFilter === 'unread' ? 'unread' : 'all',
            sentByMe: messageReadFilter === 'sentByMe',
            sendToAll: messageReadFilter === 'sentToAll' ? true : undefined,
            replyStatus:
                messageReadFilter === 'noStaffReply'
                    ? 'noStaffReply'
                    : undefined,
            month: messageMonth,
            limit: DEFAULT_PAGE_SIZE,
            pageIndex,
        });

    useLayoutEffect(() => {
        setPageIndex(1);
    }, [organizationId]);

    useEffect(() => {
        setPageIndex(1);
    }, [messageReadFilter, messageMonth, selectedMessageFilterGroupIds]);

    const { groupsById } = useOrganizationGroupsById(organizationId);

    useEffect(() => {
        if (isError instanceof Error) {
            notify(isError.message, true);
        }
    }, [isError, notify]);

    const recipientsById = useMemo(() => {
        const consumerEntries: [string, string][] = consumers.map(consumer => [
            consumer.id,
            getProfileLabel(consumer.profile, consumer.id),
        ]);

        const nokEntries: [string, string][] =
            nextOfKins?.map(nok => [
                nok.id,
                getProfileLabel(nok.profile, nok.id),
            ]) ?? [];

        const creatorEntries: [string, string][] = creators.map(creator => [
            creator.id,
            getProfileLabel(creator.profile, creator.email ?? creator.id),
        ]);

        const organizationEntries: [string, string][] = organization
            ? [
                  [
                      organization.id,
                      getProfileLabel(
                          organization.profile,
                          organization.organizationDisplayName ||
                              organization.organizationName ||
                              organization.id,
                      ),
                  ],
              ]
            : [];

        const signedInUserEntries: [string, string][] = user
            ? [[user.id, getProfileLabel(user.profile, user.id)]]
            : [];

        return new Map<string, string>([
            ...consumerEntries,
            ...nokEntries,
            ...creatorEntries,
            ...organizationEntries,
            ...signedInUserEntries,
        ]);
    }, [consumers, creators, nextOfKins, organization, user]);

    const leafGroupLabelsById = useMemo(
        () => buildLeafGroupLabelsById(consumers),
        [consumers],
    );

    const parentGroupLabelsByConsumerId = useMemo(
        () =>
            buildParentGroupLabelsByConsumerId(consumers, organization?.groups),
        [consumers, organization?.groups],
    );
    const senderBadgesByUserId = useMemo(() => {
        const entries =
            nextOfKins
                ?.map(nok => {
                    const badgeVariant = nok.isMainContact
                        ? ('main' as const)
                        : nok.isGuardian
                        ? ('guardian' as const)
                        : undefined;

                    if (!badgeVariant) {
                        return undefined;
                    }

                    return [
                        nok.id,
                        {
                            label: nok.isMainContact
                                ? t(
                                      'modules.messages.form.recipientsBadgeMainContact',
                                  )
                                : t(
                                      'modules.messages.form.recipientsBadgeGuardian',
                                  ),
                            className: badgeVariantToClassName[badgeVariant],
                        },
                    ] as const;
                })
                .filter(
                    (
                        entry,
                    ): entry is readonly [
                        string,
                        { label: string; className: string },
                    ] => Boolean(entry),
                ) ?? [];

        return new Map(entries);
    }, [nextOfKins, t]);

    const pageSize = pageInfo?.pageSize ?? DEFAULT_PAGE_SIZE;
    const totalMessages = pageInfo?.total;
    const derivedPageCount = totalMessages
        ? Math.ceil(totalMessages / pageSize)
        : messages.length
        ? Math.max(1, Math.ceil(messages.length / pageSize))
        : 0;

    useEffect(() => {
        if (derivedPageCount > 0 && pageIndex > derivedPageCount) {
            setPageIndex(derivedPageCount);
        }
    }, [derivedPageCount, pageIndex]);

    if (!organizationId) {
        return (
            <div className="text-sm text-mongooseGray">
                {t('modules.messages.form.noOrganization')}
            </div>
        );
    }

    const canSendMessages =
        !user?.supportMode &&
        (role === UserRole.creator || isOrganizationAdministrator(user));

    const targetLabelKey =
        organization?.type === OrganizationType.nursingHome
            ? 'modules.messages.targetRelatives'
            : 'modules.messages.target';

    const handlePageChange = (nextPage: number) => {
        setPageIndex(nextPage);
    };

    const handleMessageCreated = async () => {
        setPageIndex(1);
        await mutate();
    };

    const handleMessageDeleted = async () => {
        await mutate();
    };

    return (
        <div className="grid min-w-0 gap-8 lg:grid-cols-[minmax(0,1.4fr)_minmax(18rem,1fr)]">
            <Section
                title={t('modules.messages.listTitle')}
                className="min-w-0 gap-2.5"
                action={
                    canSendMessages ? (
                        <Link
                            to="/app/messages/create"
                            className="primary-btn-small flex items-center gap-1 pl-2 pr-4 py-1 lg:hidden"
                        >
                            <TextIcon SVG={PlusIcon} />
                            {t('modules.messages.createTitle')}
                        </Link>
                    ) : undefined
                }
            >
                <MessageListToolbar
                    filters={listFilters}
                    noStaffReplyTotal={isLoading ? undefined : totalMessages}
                />
                {filterLevels.length > 0 && (
                    <GroupFilterControls
                        levels={filterLevels}
                        selectedFilterCount={selectedFilterCount}
                        onChange={(levelId, nextIds) => {
                            setSelectedIdsForLevel(levelId, nextIds);
                            if (listFilters.fromAnalytics)
                                listFilters.dismissAnalytics();
                        }}
                    />
                )}
                <MessagesList
                    messages={messages}
                    isLoading={isLoading || isCreatorRoleLoading}
                    isRefreshing={isValidating}
                    consumers={consumers}
                    organizationGroups={organizationGroups}
                    groupsById={groupsById}
                    leafGroupLabelsById={leafGroupLabelsById}
                    parentGroupLabelsByConsumerId={
                        parentGroupLabelsByConsumerId
                    }
                    recipientsById={recipientsById}
                    senderBadgesByUserId={senderBadgesByUserId}
                    targetLabelKey={targetLabelKey}
                    pageCount={derivedPageCount}
                    currentPage={pageIndex}
                    onPageChange={handlePageChange}
                    onRefreshList={handleMessageDeleted}
                    refreshAfterVisibleRead={messageReadFilter !== 'unread'}
                    showCommentToggle
                />
            </Section>
            <div className="hidden min-w-0 lg:block">
                <Section
                    title={t('modules.messages.createTitle')}
                    className="min-w-0"
                >
                    {canSendMessages ? (
                        <CreateMessageForm
                            onSuccess={handleMessageCreated}
                            organizationId={organizationId}
                            canSendToAll={canSendMessagesToAll}
                        />
                    ) : (
                        <p className="text-sm text-mongooseGray">
                            {t('modules.messages.form.noPermission')}
                        </p>
                    )}
                </Section>
            </div>
        </div>
    );
};
